import React, { useState } from 'react';
import { Box, Text, Alert, Loader, Paper, Group, Stack, Anchor } from '@mantine/core';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import MembershipCompanyWidePopup from '../MembershipCompanyWidePopup';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

const SalesDepartmentPerformancePanel = () => {
    const { data, isLoading, error } = useAuthenticatedData(`${config.apiBaseUrl}/sales/department/scorecard`);

    // State for pop-up dialogs
    const [openMembership, setOpenMembership] = useState(false);
    const [openSummaryQuery, setOpenSummaryQuery] = useState(false);

    const statusColorMapping = {
        green: { background: '#2A7040', border: '#1E5A33' },
        yellow: { background: '#A07D32', border: '#7C6025' },
        red: { background: '#944848', border: '#7A3636' },
        gray: { background: '#4F4F4F', border: '#3B3B3B' }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (isNaN(value)) return 'N/A';
        return `${Math.round(value)}%`;
    };

    const openRevenuePage = (mode) => {
        const url = `/sales/department/scorecard/revenue-details`;
        window.open(url, '_blank');
    };

    const openPricingPage = (mode) => {
        const url = `/sales/department/scorecard/pricing-details`;
        window.open(url, '_blank');
    };

    const openNewBusinessGrowthPacePage = (mode) => {
        const url = `/sales/department/scorecard/new-business-growth-pace-details`;
        window.open(url, '_blank');
    };

    const openNewBusinessGrowthQtyPage = (mode) => {
        const url = `/sales/department/scorecard/new-business-growth-qty-details`;
        window.open(url, '_blank');
    };

    const openRetentionDetailsPage = (mode) => {
        const url = `/sales/department/scorecard/retention-details`;
        window.open(url, '_blank');
    };

    const MetricBox = ({ title, value, format, status, onClick }) => {
        const isKeyTarget = title.includes("YTD % Key Targets");
        const safeValue = value ?? 0;
        const hasData = safeValue !== null && !isNaN(safeValue);

        // Use the same color mapping as purchasing panel
        const { background, border } = isKeyTarget
            ? { background: '#A07D32', border: '#7C6025' }  // Orange for key targets
            : statusColorMapping[status] || { background: '#4F4F4F', border: '#3B3B3B' };

        const formattedValue =
            format === 'currency'
                ? formatCurrency(hasData ? safeValue : 0)
                : format === 'percent'
                    ? formatPercent(hasData ? safeValue : 0)
                    : hasData
                        ? format === 'number'
                            ? safeValue.toFixed(0)
                            : safeValue.toFixed(1)
                        : 'N/A';

        return (
            <Box
                onClick={onClick}
                style={{
                    backgroundColor: background,
                    padding: '1px',
                    borderRadius: '4px',
                    border: `2px solid ${border}`,
                    width: '150px',
                    textAlign: 'center',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    cursor: onClick ? 'pointer' : 'default',

                }}



            >
                <Stack gap="2" align="center">
                    <Text
                        size="xs"
                        fw={500}
                        c="white"
                        ta="center"
                        lh={1.2}
                    >
                        {title}
                    </Text>
                    <Text
                        size="xs"
                        fw={600}
                        c="white"
                        ta="center"
                    >
                        {formattedValue}
                    </Text>
                </Stack>
            </Box>
        );
    };

    if (isLoading) {
        return (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <Loader />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert color="red" title="Error">
                Error loading sales performance data: {error.message}
            </Alert>
        );
    }

    if (!data) {
        return (
            <Alert color="blue" title="No Data">
                No sales performance data available
            </Alert>
        );
    }

    // Build metrics using the server-supplied values and colors.
    const metrics = [
        {
            title: 'Booked Sales',
            goal: data.annualRevenueGoal,
            boxes: [
                {
                    title: 'Month to Date',
                    value: data.revenueMonthToDate,
                    status: data.revenueMonthToDateColor,
                    format: 'currency',
                    target: 416666,
                    mode: 'mtd'
                },
                {
                    title: 'Quarter to Date',
                    value: data.qtrToDate,
                    status: data.qtrToDateColor,
                    format: 'currency',
                    target: 1250000,
                    mode: 'qtd'
                },
                {
                    title: 'Year to Date',
                    value: data.yearToDate,
                    status: data.yearToDateColor,
                    format: 'currency',
                    target: 5000000,
                    mode: 'ytd'
                }
            ]
        },
        {
            title: 'New Business Growth',
            paceGoal: data.newBusinessGrowthPaceAnnualGoal,
            qtyGoal: data.newBusinessGrowthQtyAnnualGoal,
            paceBoxes: [
                {
                    title: 'MTD Pace',
                    value: data.newBusinessGrowthPaceMonthToDate,
                    status: data.newBusinessGrowthPaceMonthToDateColor,
                    format: 'percent',
                    target: 100,
                    mode: 'mtd'
                },
                {
                    title: 'QTD Pace',
                    value: data.newBusinessGrowthPaceQtrToDate,
                    status: data.newBusinessGrowthPaceQtrToDateColor,
                    format: 'percent',
                    target: 100,
                    mode: 'qtd'
                },
                {
                    title: 'YTD Pace',
                    value: data.newBusinessGrowthPaceYearToDate,
                    status: data.newBusinessGrowthPaceYearToDateColor,
                    format: 'percent',
                    target: 100,
                    mode: 'ytd'
                },
                {
                    title: 'YTD % Key Targets',
                    value: data.newBusinessGrowthYTDPercentKeyTargets,
                    status: data.newBusinessGrowthYTDPercentKeyTargetsColor,
                    format: 'percent',
                    target: 50
                }
            ],
            qtyBoxes: [
                {
                    title: 'MTD Qty',
                    value: data.newBusinessGrowthQtyMonthToDate,
                    status: data.newBusinessGrowthQtyMonthToDateColor,
                    format: 'number',
                    target: 2,
                    mode: 'mtd'
                },
                {
                    title: 'QTD Qty',
                    value: data.newBusinessGrowthQtyQtrToDate,
                    status: data.newBusinessGrowthQtyQtrToDateColor,
                    format: 'number',
                    target: 4,
                    mode: 'qtd'
                },
                {
                    title: 'YTD Qty',
                    value: data.newBusinessGrowthQtyYearToDate,
                    status: data.newBusinessGrowthQtyYearToDateColor,
                    format: 'number',
                    target: 16,
                    mode: 'ytd'
                }
            ]
        },
        {
            title: 'Retention',
            goal: data.retentionAnnualGoal,
            boxes: [
                {
                    title: '3M',
                    value: data.retentionCustomersPurchasedMTD,
                    status: data.retentionCustomersPurchasedMTDColor,
                    format: 'percent',
                    target: 65,
                    mode: '3m'
                },
                {
                    title: '12M',
                    value: data.retentionCustomersPurchasedYTD,
                    status: data.retentionCustomersPurchasedYTDColor,
                    format: 'percent',
                    target: 65,
                    mode: '12m'
                },
                {
                    title: 'YTD % Key Targets',
                    value: data.retentionCustomersPurchasedYTDKeyTargets,
                    status: data.retentionCustomersPurchasedYTDKeyTargetsColor,
                    format: 'percent',
                    target: 50
                }
            ]
        },
        {
            title: 'Pricing',
            goal: data.pricingAnnualGoal,
            boxes: [
                {
                    title: 'MTD',
                    value: data.pricingPaceMonthToDate,
                    status: data.pricingPaceMonthToDateColor,
                    format: 'percent',
                    target: -1,
                    mode: 'mtd'
                },
                {
                    title: 'QTD',
                    value: data.pricingPaceQtrToDate,
                    status: data.pricingPaceQtrToDateColor,
                    format: 'percent',
                    target: -1,
                    mode: 'qtd'
                },
                {
                    title: 'YTD',
                    value: data.pricingPaceYearToDate,
                    status: data.pricingPaceYearToDateColor,
                    format: 'percent',
                    target: -1,
                    mode: 'ytd'
                }
            ]
        }
    ];

    return (
        <Box style={{ padding: '1px', borderRadius: '8px', width: '100%' }}>
            {/* Top links for pop-up dialogs */}
            <Group mb="md" gap="xs">

                <Anchor
                    size="xs"
                    c="dimmed"
                    onClick={() =>
                        window.open(
                            '/query-commander/editor?queryKey=sales-department-scorecard',
                            '_blank'
                        )
                    }
                    style={{ cursor: 'pointer' }}
                >
                    Summary Query
                </Anchor>
            </Group>

            {/* Metrics Display */}
            {metrics.map((metric, metricIndex) => (
                <Box
                    key={metric.title}
                    style={{
                        marginBottom: '8px',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--mantine-color-gray-3)',
                    }}
                >
                    <Box style={{ display: 'flex', marginBottom: '6px' }}>
                        <Box style={{ width: '260px' }}>
                            <Text size="md" fw={500}>
                                {metric.title}
                            </Text>
                            {metric.title === 'New Business Growth' ? (
                                <>
                                    <Text size="xs" c="dimmed" style={{ display: 'block' }}>
                                        Annual Pace Goal: {metric.paceGoal}
                                    </Text>
                                    <Text size="xs" c="dimmed" style={{ display: 'block' }}>
                                        Annual QTY Goal: {metric.qtyGoal}
                                    </Text>
                                </>
                            ) : (
                                <Text size="xs" c="dimmed" style={{ display: 'block' }}>
                                    Annual Goal: {metric.goal}
                                </Text>
                            )}
                        </Box>

                        <Box style={{ flex: 1 }}>
                            {metric.title === 'New Business Growth' ? (
                                <Stack gap="xs">
                                    <Group gap="xs" wrap="wrap">
                                        {metric.paceBoxes.map((box, index) => (
                                            <MetricBox
                                                key={`pace-${index}`}
                                                title={box.title}
                                                value={box.value}
                                                status={box.status}
                                                format={box.format}
                                                onClick={box.mode ? () => openNewBusinessGrowthPacePage(box.mode) : null}
                                            />
                                        ))}
                                    </Group>
                                    <Group gap="xs" wrap="wrap">
                                        {metric.qtyBoxes.map((box, index) => (
                                            <MetricBox
                                                key={`qty-${index}`}
                                                title={box.title}
                                                value={box.value}
                                                status={box.status}
                                                format={box.format}
                                                onClick={box.mode ? () => openNewBusinessGrowthQtyPage(box.mode) : null}
                                            />
                                        ))}
                                    </Group>
                                </Stack>
                            ) : (
                                <Group gap="xs" wrap="wrap">
                                    {metric.boxes.map((box, index) => (
                                        <MetricBox
                                            key={index}
                                            title={box.title}
                                            value={box.value}
                                            status={box.status}
                                            format={box.format}
                                            onClick={
                                                box.mode
                                                    ? metric.title === 'Pricing'
                                                        ? () => openPricingPage(box.mode)
                                                        : metric.title === 'Retention'
                                                            ? () => openRetentionDetailsPage(box.mode)
                                                            : () => openRevenuePage(box.mode)
                                                    : null
                                            }
                                        />
                                    ))}
                                </Group>
                            )}
                        </Box>
                    </Box>
                </Box>
            ))}

            {/* Pop-up dialogs */}
            <MembershipCompanyWidePopup open={openMembership} onClose={() => setOpenMembership(false)} />
            <SQLEditorPopup
                open={openSummaryQuery}
                onClose={() => setOpenSummaryQuery(false)}
                title="Summary Query"
                endpoint={`${config.apiBaseUrl}/sales/department/scorecard/queries/sales-department-summary-query`}
            />
        </Box>
    );
};

export default SalesDepartmentPerformancePanel;